import { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { handleErrorMessage } from 'utils/errorHandler';
import { PaymentClient } from 'api/clients/PaymentClient';
import { modals } from 'utils/constants/plaidModals';
import { useMixpanel } from '../v3/hooks/useMixpanel';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'v3/hooks/useQuery';
import { useDispatch } from 'react-redux';
import { setLeadData } from 'leadData/leadSlice';

type ConnectBankAccountProps = {
  clubId: number;
  userUuid: string;
  cfUuid: string;
  shouldRedirect?: boolean;
};

export const useConnectBankAccount = ({
  clubId,
  userUuid,
  cfUuid,
  shouldRedirect = true,
}: ConnectBankAccountProps) => {
  const query = useQuery();
  const name = query.get('name');
  const navigate = useNavigate();

  const [plaidConfig, setPlaidConfig] = useState<any>({
    token: null,
  });
  const [isVerificationPending, setIsVerificationPending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPlaidFilled, setIsPlaidFilled] = useState(false);
  const [isBankAccountVerified, setIsBankAccountVerified] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    payload: modals['plaid.manual.verification'],
  });
  const dispatch = useDispatch();

  const mp = useMixpanel();

  const handleSuccess = useCallback(
    async (plaidPublicToken: any, plaidAccountId: any) => {
      setIsSubmitting(true);

      const [_data, error] = await PaymentClient().setRecurringPayments({
        cfUuid,
        plaidPublicToken,
        plaidAccountId,
      });

      if (_data) {
        console.log({ _data });
      }

      if (error) {
        handleErrorMessage(error);
      } else {
        if (name && shouldRedirect) {
          navigate(`/payment-success/?name=${name}`);
        }
      }

      setIsSubmitting(false);
    },
    [userUuid, clubId]
  );

  const onSuccess = useCallback(async (publicToken: any, metadata: any) => {
    const {
      account_id: accountId,
      account: { verification_status: verificationStatus },
    } = metadata;
    setIsSubmitting(true);

    if (verificationStatus === 'pending_manual_verification') {
      const [, error] = await PaymentClient().setPlaidManualVerification({
        cfUuid,
        plaidPublicToken: publicToken,
        plaidAccountId: accountId,
      });

      setIsVerificationPending(true);

      if (name && shouldRedirect) {
        navigate(`/microdeposits/?name=${name}`);
      }
      if (error) {
        setModal({ isOpen: true, payload: modals['plaid.error'] });
        setIsSubmitting(false);
      } else {
        setIsPlaidFilled(true);
        dispatch(setLeadData({ isPlaidCompleted: true }));
      }

      mp.fireManualVerification();
    } else {
      const [, error] = await PaymentClient().markBankAccountAsVierified({
        cfUuid,
      });
      if (error) {
        setIsSubmitting(false);
      } else setIsBankAccountVerified(true);

      setIsVerificationPending(false);

      mp.registerPlaidSuccess();
      mp.firePlaidSuccess();

      await handleSuccess(publicToken, accountId);
      dispatch(setLeadData({ isPlaidCompleted: true }));
      setIsPlaidFilled(true);
    }

    setIsSubmitting(false);
  }, []);

  const onExit = useCallback(
    (error: any, metadata: any) => {
      if (error) {
        setModal({ isOpen: true, payload: modals['plaid.error'] });
        mp.firePlaidOnExit(error.error_message);
      } else {
        setModal({ isOpen: true, payload: modals['provide.credentials'] });

        if (metadata.status) {
          mp.firePlaidOnExit(metadata.status);
        } else {
          mp.firePlaidOnExit('Microdeposits Step Incomplete');
        }
      }
    },
    [mp]
  );

  useEffect(() => {
    const createLinkToken = async () => {
      const [response, error] = await PaymentClient().createPlaidLinkToken({
        cfUuid,
      });

      if (error) {
        handleErrorMessage(error);
      }

      const { linkToken } = response;

      setPlaidConfig({
        token: linkToken,
        onSuccess,
        onExit,
      });
    };
    void createLinkToken();
  }, []);

  const { open, ready } = usePlaidLink(plaidConfig);

  return {
    open,
    ready,
    isVerificationPending,
    handleSuccess,
    isSubmitting,
    isPlaidFilled,
    isBankAccountVerified,
    modal,
    setModal,
  };
};
